import React, { useCallback, useMemo, useRef } from 'react'

import { useStore } from 'slab/hooks/useStore'

import {
  DEFAULT_DELAY_DURATION,
  DEFAULT_GRACE_PERIOD,
  TooltipContext,
  type TooltipState,
} from './context'

export interface TooltipProviderProps {
  children: React.ReactNode;
  /**
   * The delay duration in milliseconds before the tooltip is shown.
   */
  delayDuration?: number;
  /**
   * The duration in milliseconds before the tooltip is closed when the mouse
   * leaves the trigger. This defines the time the user has to seamlessly move
   * to another tooltip trigger before the current tooltip animates out.
   */
  gracePeriod?: number;
}

export function TooltipProvider ({
  children,
  delayDuration = DEFAULT_DELAY_DURATION,
  gracePeriod = DEFAULT_GRACE_PERIOD
}: TooltipProviderProps) {
  const store = useStore<TooltipState>({
    openTooltipId: null,
  })
  const wasAlreadyOpenRef = useRef(false)

  const onOpen = useCallback((tooltipId: string) => {
    wasAlreadyOpenRef.current = !!store.snapshot().openTooltipId

    store.setState({
      openTooltipId: tooltipId,
    })
  }, [])

  const onClose = useCallback((tooltipId: string) => {
    if (tooltipId === store.snapshot().openTooltipId) {
      store.setState({
        openTooltipId: null,
      })

      wasAlreadyOpenRef.current = false
    }
  }, [])

  const value = useMemo(
    () => ({
      delayDuration,
      gracePeriod,
      onOpen,
      onClose,
      store,
      wasAlreadyOpenRef,
    }),
    [delayDuration, gracePeriod, onOpen, onClose, store]
  )

  return (
    <TooltipContext.Provider value={value}>{children}</TooltipContext.Provider>
  )
}
